// eslint-disable-next-line jira/import-whitelist
import { extractProjectKey } from '@jira/platform__issue-key-parsing/src/extractProjectKey.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	getMainIssueViewAggQueryVariables,
	getMainIssueViewAggQueryFeatureFlagVariables,
} from '@jira/issue__main-issue-agg-query/src/getMainIssueViewAggQueryVariables.tsx';
// eslint-disable-next-line jira/import-whitelist
import mainIssueAggQuery from '@jira/issue__main-issue-agg-query/src/__generated__/mainIssueAggQuery$parameters.ts';
import type { mainIssueAggQuery as mainIssueAggQueryType } from '@jira/issue__main-issue-agg-query/src/__generated__/mainIssueAggQuery.graphql.ts';

import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';
import { browserOnlyResourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import type { PreloadedQuery } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { getAggOverfetchDetector } from '@jira/issue__jira-fetch-overfetch-detector/src/getAggOverfetchDetector.tsx';

import { fg } from '@atlassian/jira-feature-gating';

import type { RouteResource } from '@atlassian/react-resource-router';
import { createRelayResourceDeprecated } from './withResourceEarlyData.tsx';

export const issueViewRelayResource: RouteResource<
	PreloadedQuery<mainIssueAggQueryType, Record<string, unknown>>
> = browserOnlyResourceWithCondition(
	() => {
		if (__SERVER__) {
			// fetch it in modern SSR, but not in legacy SSR
			return false;
		}
		return true;
	},
	createRelayResourceDeprecated<mainIssueAggQueryType>({
		type: 'ISSUE_RELAY_DATA',
		isBrowserOnly: true,
		captureErrors: true,
		captureTraceIds: !__SERVER__,
		getQuery: (routerContext, { tenantContext: { cloudId } }) => {
			const { match } = routerContext;
			let issueKey = getIssueKey(match);

			if (
				!issueKey &&
				(fg('nin_glance_panel_technical_changes') || fg('nin_issue_app_conditional_entrypoint'))
			) {
				issueKey = routerContext.query?.selectedIssue ?? '';
			}

			const projectKey = issueKey ? extractProjectKey(issueKey) : '';
			const queryVariables = {
				cloudId,
				issueKey: issueKey || '',
				projectKey,
			};
			if (issueKey && fg('jfp-magma-mainissueaggquery-called-more-detector')) {
				getAggOverfetchDetector().setInitialIssueKey(issueKey);
			}

			return {
				parameters: mainIssueAggQuery,
				variables: {
					...queryVariables,
					...getMainIssueViewAggQueryVariables(issueKey),
				},
				// Pass over the variables that actually would trigger a reload when they are changed=
				keyVariables: {
					...queryVariables,
					...getMainIssueViewAggQueryFeatureFlagVariables(),
				},
				options: {
					fetchPolicy:
						issueKey ||
						!(
							fg('nin_glance_panel_technical_changes') || fg('nin_issue_app_conditional_entrypoint')
						)
							? 'store-and-network'
							: 'store-only',
				},
			};
		},
		onFetchComplete: (config) => {
			if (fg('jfp-magma-mainissueaggquery-called-more-detector')) {
				const issueKey = config?.variables?.issueKey;
				if (issueKey) {
					getAggOverfetchDetector().onRouterResourceFetchComplete(issueKey);
				}
			}
		},
	}),
);

// No need to FF usage of this hook, the resource is internally FF'd
export const useIssueViewRelayRouteResource = (options: {
	suspendWhenLoading?: boolean;
}): {
	queryReference: PreloadedQuery<mainIssueAggQueryType, Record<string, unknown>> | null;
} => useRelayResource<mainIssueAggQueryType>(issueViewRelayResource, options);
